<template>
    <page-template page-id="reg-page" ref="super">

        <document-title :title="$tb('pages.confirmProfile.documentTitle')"></document-title>

        <template v-slot:before v-if="isTitlebarActive">
            <titlebar :text="$t('pages.login.title')"></titlebar>
        </template>

        <div class="ui column">

            <brand-header :text="$t('pages.confirmProfile.header', {0: $store.state.appName})"
                          :has-extension="hasHeaderExtension"
                          :has-logo="hasBrandLogo">
            </brand-header>

            <p v-html="$t('pages.confirmProfile.subheader')"></p>

            <messages id="Notifications" ref="postman"></messages>
            <label for="username" class="hidden">{{$t('common.label.username')}}</label>
            <input type="text" id="username" name="username" v-model="username" class="hidden">

            <div class="two fields">
                <validated-field :show-errors-on="showErrors" :has-any-errors="$v.firstname.$invalid"
                                 :validation-rules="[{value: $v.firstname.required, msg: this.$t('common.validation.field.empty')}]">
                    <labeled-input-field id="firstname"
                                         :label="$t('common.label.firstname')"
                                         :mandatory="true"
                                         :focused="true"
                                         v-model="$v.firstname.$model"
                                         @enter-pressed.prevent="onContinue"
                                         :invalid="$v.firstname.$invalid && showErrors"></labeled-input-field>
                </validated-field>
                <validated-field :show-errors-on="showErrors"
                                 :has-any-errors="$v.lastname.$invalid"
                                 :validation-rules="[{value: $v.lastname.required, msg: this.$t('common.validation.field.empty')}]">
                    <labeled-input-field id="lastname"
                                         :label="$t('common.label.lastname')"
                                         :mandatory="true"
                                         v-model="$v.lastname.$model"
                                         @enter-pressed.prevent="onContinue"
                                         :invalid="$v.lastname.$invalid && showErrors"></labeled-input-field>
                </validated-field>
            </div>

            <validated-field
                    id="country-errors"
                    :show-errors-on="showErrors" :has-any-errors="$v.country.$invalid"
                    :validation-rules="[{value: $v.country.required, msg: this.$t('common.validation.field.empty')}]">
                <dropdown-select v-if="isMobile()" id="country" name="country" v-model="$v.country.$model"
                                 classes="ui fluid dropdown selection"
                                 :placeholder="$t('common.label.country')"
                                 :mandatory="true"
                                 :options="countryOptions"
                                 @enter-pressed.prevent="onContinue"
                                 :invalid="$v.country.$invalid && showErrors"></dropdown-select>

                <dropdown-mk2 v-else id="country" v-model="$v.country.$model" name="country"
                              :placeholder="$t('common.label.country')"
                              :options="countryOptions"
                              :mandatory="true"
                              aria-error-element-id="country-errors"
                              @enter-pressed.prevent="onContinue"
                              :invalid="$v.country.$invalid && showErrors" ></dropdown-mk2>
            </validated-field>

            <mandatory-label></mandatory-label>

            <validated-field v-if="!hasPass"
                    id="password-errors"
                    :show-errors-on="showErrors" :has-any-errors="$v.password.$invalid"
                    :validation-rules="[{value: $v.password.requiredIf, msg: this.$t('common.validation.field.empty')},
                                                 {value: $v.password.guidelines, msg: $t('common.validation.password.guidelines')},
                                                 {value: $v.password.noInvalidCharacters, showOn: $v.password.guidelines, msg: $t('common.validation.password.invalidCharacter')}]">
                <password-input-field id="password"
                                      autocomplete="new-password"
                                      :label="$t('common.label.password')"
                                      @enter-pressed.prevent="onContinue"
                                      v-model="$v.password.$model"
                                      :mandatory="isPasswordMandatory()"
                                      aria-error-element-id="aria-password-rules-summary password-errors aria-password-status"
                                      :aria-label-text="$t('common.label.password')"
                                      :invalid="$v.password.$invalid && showErrors"></password-input-field>

                <span id="aria-password-rules-summary" style="height:0; overflow:hidden; position:absolute;"
                      v-if="!password">
                    {{ $t('components.password.aria.rules.summary') }}
                </span>
            </validated-field>
            <password-denylist
                v-if="password && pwdDenyListValidationEnabled && !hasPass"
                :password="password" :first-name="firstname" :last-name="lastname" :user-name="username">
            </password-denylist>
            <password-rules :password="password" v-if="!!password && !hasPass"
                            aria-status-element-id="aria-password-status">
            </password-rules>

            <template>
                <!-- TODO: Remove this once new toas checkbox structure is rolled out -->
                <legal-text-display v-if="!!requestedLegalTexts && !(requestedLegalTexts.hasOwnProperty('checkboxList') || requestedLegalTexts.hasOwnProperty('consentCategories'))" ref="toas"
                                    :requested-legal-texts="requestedLegalTexts"
                                    :show-errors="showErrors"
                                    v-on:accepted="checkHideErrors"
                                    :country="country"></legal-text-display>
                <possible-legal-texts v-if="!!requestedLegalTexts && requestedLegalTexts.hasOwnProperty('checkboxList')" ref="toas"
                                      :requested-legal-texts="requestedLegalTexts"
                                      :show-errors="showErrors"
                                      v-on:accepted="checkHideErrors"
                                      :country="country"></possible-legal-texts>
                <possible-user-consents v-if="!!requestedLegalTexts && requestedLegalTexts.hasOwnProperty('consentCategories')" ref="toas"
                                        :requested-legal-texts="requestedLegalTexts"
                                        :show-errors="showErrors"
                                        :country="country"
                                        v-on:accepted="checkHideErrors"></possible-user-consents>
            </template>

            <mandatory-label v-if="!!requestedLegalTexts"></mandatory-label>

            <div class="field">
                <button id="continue" type="submit" @click.prevent="onContinue"
                        :disabled="!confirmationPossible" class="ui fluid large primary submit button">
                    {{$t('common.button.continue')}}
                </button>
            </div>

            <div class="field">
                <router-link id="back" to="/" class="ui fluid basic button">
                    {{ $t('common.button.back') }}
                </router-link>
            </div>
        </div>

    </page-template>
</template>

<script>
import PageTemplate from './template/PageTemplate.vue'
import Titlebar from '../components/Titlebar.vue'
import BrandHeader from '../components/BrandHeader.vue'
import LabeledInputField from '../components/LabeledInputField.vue'
import Messages from '../components/Messages.vue'
import MandatoryLabel from '../components/MandatoryLabel.vue'
import DropdownSelect from '../components/DropdownSelect.vue'
import LegalTextDisplay from '../components/toas/LegalTextDisplay.vue'
import ValidatedField from '../components/ValidatedField.vue'
import PasswordInputField from '../components/LabeledPasswordInputField.vue'
import PasswordRules from '../components/PasswordRules.vue'
import DropdownMk2 from '../components/DropdownMk2.vue'
import PossibleLegalTexts from '../components/toas/PossibleLegalTexts.vue'
import PossibleUserConsents from '../components/toas/PossibleUserConsents'
import { mixin } from '../js/ciam-mixin.js'
import { brand } from '../js/brand.js'
import * as validate from '../js/validation.js'
import { required, requiredIf } from 'vuelidate/lib/validators'
import PasswordDenylist from '../components/PasswordDenylist'

export default {
  components: {
    PossibleLegalTexts,
    PossibleUserConsents,
    PageTemplate,
    Titlebar,
    BrandHeader,
    Messages,
    LabeledInputField,
    MandatoryLabel,
    DropdownSelect,
    DropdownMk2,
    LegalTextDisplay,
    ValidatedField,
    PasswordInputField,
    PasswordRules,
    PasswordDenylist
  },
  mixins: [mixin, brand],
  props: {
    uid: {
      type: String
    },
    formPrefill: {
      type: Object
    },
    preloadedLegalTexts: {
      type: Object,
      default: function () {
        return null
      }
    },
    passwordMandatory: {
      type: Boolean,
      default: function () {
        return false
      }
    },
    hasPass: {
      type: Boolean,
      default: false
    }
  },
  data: function () {
    return {
      // username: this.$store.state.contactMedium === 'email' ? this.$store.state.email : this.$store.state.mobile,
      username: this.$store.state.username,
      firstname: this.formPrefill ? this.formPrefill.firstname : '',
      lastname: this.formPrefill ? this.formPrefill.lastname : '',
      country: this.formPrefill && this.formPrefill.country ? this.formPrefill.country.toUpperCase() : '',
      touchpoint: this.getTouchpointCountry(this.formPrefill.country.toUpperCase()),
      language: this.$store.state.lang,
      requestedLegalTexts: this.preloadedLegalTexts ? this.preloadedLegalTexts : null,
      confirmationPossible: true,
      showErrors: false,
      password: '',
      pwdDenyListValidationEnabled: this.$store.state.pwdDenyListValidationEnabled ? this.$store.state.pwdDenyListValidationEnabled : false
    }
  },
  validations () {
    return {
      firstname: { required },
      lastname: { required },
      country: { required },
      password: {
        requiredIf: requiredIf(() => {
          if (this.hasPass) return false
          return this.isPasswordMandatory()
        }),
        guidelines (input) {
          // if pw is empty we dont need to check the guidelines
          return this.password !== '' ? validate.passwordRulesSatisfied(input) : true
        },
        noInvalidCharacters (input) {
          return !validate.containsInvalidCharacter(input)
        }
      }
    }
  },
  methods: {
    onContinue: function () {
      if (!this.uid) return

      //  remove previous errors
      this.$refs.postman.clear()
      this.showErrors = false
      this.username = this.username?.replace(/＠/g, '@')

      if (!this.isFormValid()) return

      this.$refs.super.startLoading()

      const touchpoint = this.touchpoint ? this.touchpoint : this.country

      const payload = this.$api.payloads.buildConfirmProfilePayload(this.firstname, this.lastname, this.country, this.password,
        touchpoint, this.$refs.toas.getAcceptedIds())
      this.$api.register.confirmProfile(this.uid, payload, this.onSuccess, this.onFail)
    },
    isFormValid: function () {
      if (this.$v.$anyError || this.$v.$invalid || this.$refs.toas.$v.$invalid) {
        const _this = this

        this.focusFirstErrorField(function () {
          _this.showErrors = true
        })

        return false
      }

      return true
    },
    onSuccess: function (resp) {
      this.$store.commit('country', this.country)
      this.handleRoutingResponse(resp)
    },
    onFail: function (response) {
      this.onAjaxError(response)
      this.$refs.super.stopLoading()
    },
    goBack: function () {
      this.$router.push('/')
    },
    fetchLegalTexts () {
      if (!this.country || !this.language || !this.uid) return

      this.$refs.super.startLoading()
      this.$refs.postman.clear()

      this.confirmationPossible = false

      const touchpoint = this.touchpoint ? this.touchpoint : this.country

      console.debug(`Fetching legal texts [uid=${this.uid}, homeCountry=${this.country}, touchpoint=${touchpoint}, language=${this.language}]`)

      const payload = this.$api.payloads.buildLegalTextsPayload(this.country, touchpoint, this.language)
      this.$api.toas.fetchRegistrationTexts(payload, this.setupLegalTexts, this.onToasFetchError)
    },
    setupLegalTexts (resp) {
      this.requestedLegalTexts = resp
      this.confirmationPossible = true
      this.$refs.super.stopLoading()
    },
    onToasFetchError () {
      console.debug('Failed to fetch legal texts, disable registration')
      this.requestedLegalTexts = undefined
      $('.ui.form').form('add errors', [this.$t('errors.registration.loadLegalTexts')])
      this.$refs.super.stopLoading()
    },
    checkHideErrors: function () {
      if (this.$v.$invalid) return
      if (this.$refs.toas && this.$refs.toas.$v.$invalid) return

      this.showErrors = false
    },
    isPasswordMandatory: function () {
      return this.$store.state.acr.includes('MFA') ||
        this.$store.state.acr.includes('PASSWORD') ||
        this.passwordMandatory
    },
    checkAuthorization: function () {
      if (!this.uid) {
        console.debug('Opened this page in an unintended way!')
        $('.ui.form').form('add errors', [this.$t('pages.confirmProfile.notAllowed')])
      }
    }
  },
  computed: {
    countryOptions: function () {
      return this.$tCountries(false)
    }
  },
  mounted: function () {
    console.log(`Confirm preconfigured account [formPrefill=${JSON.stringify(this.formPrefill)}, requestedLegalTexts=${JSON.stringify(this.requestedLegalTexts)}}`)
    this.checkAuthorization()
  },
  watch: {
    country: {
      handler () {
        this.fetchLegalTexts()
      }
    },
    $v: {
      handler () {
        this.checkHideErrors()
      },
      deep: true
    }
  }
}
</script>

<style scoped>
</style>
